import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Calculator, Info } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import api, { formatApiError, getUTM } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import CurrencySelect from "@/components/site/CurrencySelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PLATFORMS = ["Google Ads", "Meta Ads", "YouTube Ads", "LinkedIn Ads", "TikTok Ads", "Multiple platforms"];

export default function QuoteCalculator() {
  const { config, currency, symbol, price } = useCurrency() || {};
  const navigate = useNavigate();
  const [pkgId, setPkgId] = useState("growth");
  const [platform, setPlatform] = useState("Google Ads");
  const [budget, setBudget] = useState(1000);
  const [duration, setDuration] = useState(3);
  const [addons, setAddons] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", country: "" });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const packages = config?.packages || [];
  const allAddons = config?.addons || [];
  const tax = config?.tax || {};
  const pkg = packages.find((p) => p.id === pkgId);

  const calc = useMemo(() => {
    const feeP = price?.(pkg?.prices);
    const fee = feeP?.val || 0;
    const custom = feeP == null;
    const feeTotal = fee * duration;
    let addTotal = 0;
    addons.forEach((id) => { const a = allAddons.find((x) => x.id === id); const p = price?.(a?.prices); if (p) addTotal += p.val; });
    const subtotal = feeTotal + addTotal;
    const taxAmt = tax?.enabled ? Math.round((subtotal * (tax.percent || 0)) / 100 * 100) / 100 : 0;
    return { fee, custom, feeTotal, addTotal, subtotal, taxAmt, total: subtotal + taxAmt };
  }, [pkg, duration, addons, price, allAddons, tax]);

  const toggleAddon = (id) => setAddons((a) => a.includes(id) ? a.filter((x) => x !== id) : [...a, id]);
  const fmt = (n) => `${symbol}${Number(n).toLocaleString()}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please accept the consent to continue."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/proposals", {
        ...form, currency, service: platform, platform, package_id: pkgId,
        ad_budget: Number(budget), duration_months: Number(duration), addons, consent, utm: getUTM(),
      });
      toast.success("Proposal created!");
      navigate(`/proposal/${data.proposal.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!config) return <div className="bs-card p-8 text-center text-muted-foreground">Loading calculator...</div>;

  return (
    <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]" data-testid="quote-calculator">
      <div className="space-y-6 bs-card p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-xl font-bold"><Calculator className="h-5 w-5 text-primary" /> Build your quote</h3>
          <CurrencySelect />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Package</Label>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            {packages.map((p) => {
              const pp = price?.(p.prices);
              return (
                <button key={p.id} type="button" onClick={() => setPkgId(p.id)} data-testid={`calc-pkg-${p.id}`}
                  className={`rounded-xl border p-4 text-left transition-all ${pkgId === p.id ? "border-primary bg-primary/5 shadow-glow" : "border-border hover:border-primary/40"}`}>
                  <p className="font-display font-bold">{p.name}</p>
                  <p className="text-sm text-muted-foreground">{pp ? `${fmt(pp.val)}/${p.period}` : "Custom"}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Advertising platform</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="mt-1.5" data-testid="calc-platform"><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Management duration (months)</Label>
            <Input type="number" min={1} max={24} value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1.5" data-testid="calc-duration" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Monthly advertising budget ({currency}) — you fund this directly</Label>
            <Input type="number" min={0} value={budget} onChange={(e) => setBudget(e.target.value)} className="mt-1.5" data-testid="calc-budget" />
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Optional add-ons</Label>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {allAddons.map((a) => {
              const pp = price?.(a.prices);
              return (
                <label key={a.id} className={`flex cursor-pointer items-center justify-between gap-2 rounded-lg border p-3 text-sm ${addons.includes(a.id) ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`calc-addon-${a.id}`}>
                  <span className="flex items-center gap-2"><Checkbox checked={addons.includes(a.id)} onCheckedChange={() => toggleAddon(a.id)} /> {a.name}</span>
                  <span className="text-muted-foreground">{pp ? fmt(pp.val) : "—"}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* summary + details */}
      <form onSubmit={submit} className="h-fit space-y-4 bs-card p-6" data-testid="calc-summary">
        <h3 className="font-display text-lg font-bold">Your estimate</h3>
        <div className="space-y-2 text-sm">
          <Row label={`Service fee (${fmt(calc.fee)} × ${duration})`} value={calc.custom ? "Custom" : fmt(calc.feeTotal)} />
          <Row label="Add-ons" value={fmt(calc.addTotal)} />
          <Row label="Subtotal" value={calc.custom ? "Custom" : fmt(calc.subtotal)} />
          {tax?.enabled && <Row label={`${tax.label} (${tax.percent}%)`} value={fmt(calc.taxAmt)} />}
          <div className="my-2 h-px bg-border" />
          <div className="flex items-center justify-between text-base font-bold">
            <span>Total service cost</span><span data-testid="calc-total">{calc.custom ? "Custom quote" : fmt(calc.total)}</span>
          </div>
        </div>
        <div className="rounded-lg border border-accent/30 bg-accent/5 p-3 text-xs text-muted-foreground">
          <p className="flex items-start gap-2"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
            Separately, your estimated advertising platform budget is <strong>{fmt(Number(budget) * duration)}</strong> ({fmt(budget)}/mo × {duration}), billed by the platform and controlled by you. No leads, sales or ROI are guaranteed.</p>
        </div>

        <div className="space-y-3 border-t border-border pt-4">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="calc-name" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="calc-email" /></div>
            <div><Label className="text-xs">Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1" /></div>
          </div>
          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <Checkbox checked={consent} onCheckedChange={setConsent} className="mt-0.5" data-testid="calc-consent" />
            I consent to Buddy Solution processing my details to prepare this proposal, per the Privacy Policy.
          </label>
          <Button type="submit" size="lg" className="w-full rounded-full" disabled={busy} data-testid="calc-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Generate proposal
          </Button>
        </div>
      </form>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between text-muted-foreground"><span>{label}</span><span className="font-medium text-foreground">{value}</span></div>;
}
